import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  TextInput,
  Stack,
  Title,
  Text,
  Modal,
  Group,
} from '@mantine/core';
import { shallow } from 'zustand/shallow';

import { PageTitle } from '@components/shared';
import { BasicButton } from '@components/Button';
import PasskeyManager from '@components/Auth/PasskeyManager';
import PasswordForm from '@components/Auth/PasswordForm';
import appStore from '@duck/app-store';
import entityIcon from '@util/entityIcon';
import entityColor from '@util/entityColor';

export default function AdminProfile() {
  const { admin, setAdmin } = appStore(
    (state) => ({
      admin: state.admin,
      setAdmin: state.setAdmin,
    }),
    shallow,
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const adminId = admin?.admin_id;

  useEffect(() => {
    if (!adminId) return;

    axios
      .get(`/admins/${adminId}/`)
      .then(({ data }) => {
        const profile = data.response[0];
        setFirstName(profile.first_name || '');
        setLastName(profile.last_name || '');
        setEmail(profile.email || '');
        setAdmin({
          ...admin,
          full_name: profile.full_name,
          email: profile.email,
        });
      })
      .catch((err) => toast.error(err))
      .finally(() => setLoading(false));
  }, [adminId]);

  function onSave() {
    setSaving(true);
    axios
      .put(`/admins/${adminId}/`, {
        first_name: firstName,
        last_name: lastName,
        email,
      })
      .then(({ data }) => {
        const profile = data.response[0];
        setSaving(false);
        toast.success('Profile saved');
        setAdmin({
          ...admin,
          full_name: profile.full_name,
          email: profile.email,
        });
      })
      .catch((err) => {
        setSaving(false);
        toast.error(err);
      });
  }

  if (loading) {
    return <Text c="dimmed">Loading profile…</Text>;
  }

  return (
    <div>
      <PageTitle
        title="Profile"
        icon={entityIcon.admin()}
        iconColor={entityColor.home}
      />
      <Title order={3} mb="sm">
        Account
      </Title>
      <Stack gap="md" maw={420} mb="xl">
        <TextInput
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextInput
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <TextInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Group>
          <BasicButton
            text="Save profile"
            onClick={onSave}
            loading={saving}
            disabled={saving}
            color="var(--brand-brick)"
            variant="filled"
            style={{
              background: 'var(--brand-brick)',
              borderColor: 'var(--brand-brick)',
            }}
            primary
          />
          <BasicButton
            text="Change password"
            onClick={() => setPasswordOpen(true)}
            variant="outline"
            color="gray"
          />
        </Group>
      </Stack>

      <PasskeyManager />

      <Modal
        opened={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        title="Change password"
        centered
      >
        <PasswordForm
          updating
          additionalReqData={{ admin_id: adminId }}
          onSuccess={() => setPasswordOpen(false)}
        />
      </Modal>
    </div>
  );
}
